import { useState, useCallback, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import type {
    AppointmentSlot,
    BookingEntrySource,
    NewRepairBookingSession,
    Shop,
} from '../models/repair-bookings';
import { apiCallWithAlertOnError, fetchShops, startSession } from '../utils/api';

const getInitialPageState = (entrySource: BookingEntrySource): BookingCreatePageState =>
    entrySource === 'console'
        ? { status: 'product-selection', groupKey: 'root' }
        : { status: 'precaution' };

type BookingCreatePageState =
    | { status: 'precaution' }
    | { status: 'product-selection'; groupKey: string }
    | { status: 'purpose-selection' }
    | { status: 'shop-selection' }
    | { status: 'date-selection' }
    | { status: 'time-selection'; selectedDate: string; slots: AppointmentSlot[] }
    | { status: 'personal-info' }
    | { status: 'confirm' }
    | { status: 'complete'; confirmationId: string }
    | { status: 'invalid' };

type BookingCreateInitialState = Extract<BookingCreatePageState, { status: 'precaution' }>;
type BookingCreateCompleteState = Extract<BookingCreatePageState, { status: 'complete' }>;
type BookingCreateOtherState = Exclude<
    BookingCreatePageState,
    BookingCreateInitialState | BookingCreateCompleteState
>;

const BOOKING_STEPS: string[] = ['目的選択', '店舗選択', '日時選択', '情報入力', '内容確認'];
const BOOKING_STEPS_WITHOUT_SHOP_SELECTION: string[] = [
    '目的選択',
    '日時選択',
    '情報入力',
    '内容確認',
];

const PAGE_CONFIG: Record<
    BookingCreatePageState['status'],
    { isShopSelected: boolean; stepIndex: number | null }
> = {
    precaution: { isShopSelected: false, stepIndex: null },
    'product-selection': { isShopSelected: false, stepIndex: 0 },
    'purpose-selection': { isShopSelected: false, stepIndex: 0 },
    'shop-selection': { isShopSelected: false, stepIndex: 1 },
    'date-selection': { isShopSelected: true, stepIndex: 2 },
    'time-selection': { isShopSelected: true, stepIndex: 2 },
    'personal-info': { isShopSelected: true, stepIndex: 3 },
    confirm: { isShopSelected: true, stepIndex: 4 },
    complete: { isShopSelected: false, stepIndex: null },
    invalid: { isShopSelected: false, stepIndex: null },
};

const SHOP_SELECTED_PAGE_CONFIG: Record<
    Exclude<BookingCreatePageState['status'], 'shop-selection'>,
    { isShopSelected: boolean; stepIndex: number | null }
> = {
    precaution: { isShopSelected: false, stepIndex: null },
    'product-selection': { isShopSelected: true, stepIndex: 0 },
    'purpose-selection': { isShopSelected: true, stepIndex: 0 },
    'date-selection': { isShopSelected: true, stepIndex: 1 },
    'time-selection': { isShopSelected: true, stepIndex: 1 },
    'personal-info': { isShopSelected: true, stepIndex: 2 },
    confirm: { isShopSelected: true, stepIndex: 3 },
    complete: { isShopSelected: false, stepIndex: null },
    invalid: { isShopSelected: false, stepIndex: null },
};

const BOOKING_CREATE_BASE_PATH: Record<BookingEntrySource, string> = {
    web: '/bookings/new',
    console: '/console/bookings/new',
};

const useBookingCreatePages = (entrySource: BookingEntrySource, csrfToken: string) => {
    const [query] = useSearchParams();
    const [requestedShopId] = useState(() => query.get('shop'));
    const [pageStack, setPageStack] = useState<BookingCreatePageState[]>([
        getInitialPageState(entrySource),
    ]);
    const [session, setSession] = useState<NewRepairBookingSession | null>(null);

    const { index } = useParams();
    const pageIndex = !index ? 0 : index === 'complete' ? pageStack.length - 1 : Number(index);
    const navigate = useNavigate();
    const basePath = BOOKING_CREATE_BASE_PATH[entrySource];

    const currentPage =
        pageIndex < pageStack.length ? pageStack[pageIndex]! : { status: 'invalid' as const };
    const pageConfig =
        session?.designatedShop && currentPage.status !== 'shop-selection'
            ? SHOP_SELECTED_PAGE_CONFIG[currentPage.status]
            : PAGE_CONFIG[currentPage.status];

    useEffect(() => {
        if (currentPage.status !== 'product-selection' || session) {
            return;
        }
        void apiCallWithAlertOnError(async () => {
            const response = await startSession(csrfToken, 'new');
            let designatedShop: Shop | null = null;
            if (requestedShopId) {
                const { shops } = await fetchShops(csrfToken, response.session_id);
                designatedShop = shops.find((shop) => shop.shop_id === requestedShopId) ?? null;
            }
            setSession({
                type: 'new',
                csrfToken,
                sessionId: response.session_id,
                designatedShop,
            });
        })();
    }, [currentPage.status, session, csrfToken, requestedShopId]);

    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setPageStack((stack) =>
            stack.length > pageIndex + 1 ? stack.slice(0, pageIndex + 1) : stack
        );
    }, [pageIndex, setPageStack]);

    const pushPageStack = useCallback(
        (state: BookingCreateOtherState) => {
            setPageStack((stack) => stack.slice(0, pageIndex + 1).concat(state));
            navigate(`${basePath}/${pageIndex + 1}/`);
        },
        [basePath, pageIndex, setPageStack, navigate]
    );

    const popPageStack = useCallback(() => {
        navigate(-1);
    }, [navigate]);

    const complete = useCallback(
        (confirmationId: string) => {
            setPageStack([{ status: 'complete', confirmationId }]);
            navigate(`${basePath}/complete/`);
        },
        [basePath, setPageStack, navigate]
    );

    const clearPageStack = useCallback(() => {
        setSession(null);
        setPageStack([getInitialPageState(entrySource)]);
        navigate(`${basePath}/`);
    }, [entrySource, navigate, basePath]);

    return {
        currentPage,
        pageConfig,
        pushPageStack,
        complete,
        popPageStack,
        clearPageStack,
        steps: session?.designatedShop ? BOOKING_STEPS_WITHOUT_SHOP_SELECTION : BOOKING_STEPS,
        session,
    };
};

export default useBookingCreatePages;
